<template>
  <div>
    <form-alert :message="alert.message" :type="alert.type" />

    <div v-if="loadingTypes" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <template v-else>

      <!-- Combined / Conjunction toggles -->
      <div class="f-form">
        <div class="f-form__full">
          <div class="alert alert-info alert-simple mb-3">
            For an overview of how events work and what each setting is responsible for, see the
            <button type="button" class="btn btn-link p-0 align-baseline" @click="showGuide = true">event guide</button>.
          </div>
          <div
            v-if="brotherId == null && isCombined && !sharedForm.is_conjunction"
            class="alert alert-info alert-simple mb-3"
          >
            This is a combined event. When nominating, competitors will have three options to select from: Event 1, Event 2, or Event 1 & 2.
          </div>
          <div
            v-if="brotherId == null && sharedForm.is_conjunction"
            class="alert alert-info alert-simple mb-3"
          >
            This is a conjunction event. When nominating, competitors will have two options to select from:
            Event 1 or Event 1 &amp; 2.
          </div>
          <div
            v-if="brotherId != null && sharedForm.is_conjunction"
            class="alert alert-info alert-simple mb-3"
          >
            The original conjunction event has been split into 2 identical combined events, A and B,
            plus an initially empty Event 2 Final. This action cannot be undone.
            Nominations from the original conjunction event have been divided equally between the new A
            and B events. Individual nominations can be moved from A to B and vice versa in the Accepted
            Nominations area.
            A and B have their own separate First Round and final. The Event 2 Final will be populated
            based on the minimum score set on the Draws page for events A and B.
          </div>
          <div
            v-else-if="brotherId != null && isCombined"
            class="alert alert-info alert-simple mb-3"
          >
            The original combined event has been split into 2 identical combined events, A and B. This
            action cannot be undone. Nominations from the original event have been divided equally between
            the new A and B events. Individual nominations can be moved from A to B and vice versa in the
            Accepted Nominations area. Each combined event has a joint First Round and a separate final,
            resulting in four separate finals in total.
          </div>
          <div class="form-group">
            <div class="d-flex align-items-center">
              <f-checkbox
                :key="`combined_${checkboxKey}`"
                id="is_combined"
                label="Combined events"
                :value="isCombined"
                :lock-if-true="lockToggles"
                :disabled="brotherId != null || !firstEventCombinable"
                :font-normal="true"
                @input="onCombinedInput"
              />
              <f-checkbox
                v-if="isCombined"
                :key="`conjunction_${checkboxKey}`"
                id="is_conjunction"
                label="Conjunction events"
                :value="sharedForm.is_conjunction"
                :lock-if-true="lockToggles"
                :disabled="brotherId != null && !sharedForm.is_conjunction"
                class="ml-4"
                :font-normal="true"
                @input="onConjunctionInput"
              />
            </div>
          </div>

          <doc-modal v-if="showGuide" doc="events.guide" :context="{ eventTypes }" @close="showGuide = false" />

          <confirm-modal
            v-if="confirmPending"
            :message="confirmPending === 'combined'
              ? 'Enabling Combined events will add a second event to this group and this action cannot be undone. Are you sure?'
              : 'Enabling Conjunction event will set Cost with a discount to the sum of event costs and this action cannot be undone. Are you sure?'"
            @confirm="onConfirm"
            @cancel="onCancelConfirm"
          />
        </div>
      </div>

      <div class="f-form">
        <event-fields-name-section
          :value="events[0]"
          :errors="errors"
          :index="0"
          @input="$emit('update-event', 0, $event)"
        />

        <div class="f-form__half">
          <f-text
            id="declared_prize"
            label="Declared Prize Money"
            type="number"
            :disabled="!hasDeclaredPrize"
            :value="sharedForm.declared_prize"
            :error="sharedError('declared_prize')"
            @input="$emit('update-shared', 'declared_prize', $event)"
          />
        </div>

        <event-fields-name-section
          v-if="isCombined"
          :value="events[1]"
          :errors="errors"
          :index="1"
          @input="$emit('update-event', 1, $event)"
        />

        <div v-if="isCombined && !sharedForm.is_conjunction" class="f-form__half f-form__half--solo">
          <f-text
            id="discount_price"
            label="Cost with a discount"
            type="number"
            min="0"
            hint="Applied if participating in both events"
            :value="sharedForm.discount_price"
            :error="sharedError('discount_price')"
            @input="$emit('update-shared', 'discount_price', $event)"
          />
        </div>
      </div>

      <!-- Event 1 -->
      <h3 v-if="isCombined" class="h3 mb-3">Event 1 restrictions</h3>
      <event-fields-section
        :value="events[0]"
        :event-types="isCombined ? combinableEventTypes : eventTypes"
        :loading-types="false"
        :is-combined="isCombined"
        :errors="errors"
        :index="0"
        @input="$emit('update-event', 0, $event)"
      />

      <!-- Event 2 (combined only) -->
      <template v-if="isCombined">
        <h3 class="h3 mb-3 mt-3">Event 2 restrictions</h3>
        <event-fields-section
          :value="events[1]"
          :event-types="combinableEventTypes"
          :loading-types="false"
          :is-combined="isCombined"
          :errors="errors"
          :index="1"
          @input="$emit('update-event', 1, $event)"
        />
      </template>

      <!-- Number of Judges + Judges selection + Sponsor Logo -->
      <div class="f-form mt-3">
        <div class="f-form__pair">
          <f-search-select
            id="judges_num"
            label="Number of Judges"
            :value="sharedForm.judges_num"
            :options="judgesOptions"
            :searchable="false"
            :disabled="!hasJudges"
            track-by="id"
            option-label="name"
            :error="sharedError('judges_num')"
            @input="onJudgesNumChange"
          />
        </div>
        <div class="f-form__pair">
          <f-file
            id="sponsor_logo"
            label="Sponsor Logo"
            accept=".jpg,.jpeg,.png,.webp"
            hint="jpg, jpeg, png, webp — max 2 MB"
            :max-size="2097152"
            @change="onLogoChange"
          />
          <f-image-preview
            v-if="sponsorLogoUrl"
            :src="sponsorLogoUrl"
            :deletable="true"
            :deleting="deletingLogo"
            @delete="$emit('remove-logo')"
          />
        </div>
        <div class="f-form__half f-form__half--3-1">
          <f-search-select
            id="judge_ids"
            label="Judges"
            :value="judgeIds"
            :options="availableJudges"
            :multiple="true"
            :disabled="!hasJudges"
            track-by="id"
            :custom-label="judgeLabel"
            placeholder="Select judge"
            :loading="loadingJudges"
            :error="sharedError('judge_ids')"
            @input="$emit('update-judge-ids', $event)"
            @search-change="$emit('search-judges', $event)"
          />
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import ConfirmModal from '@/components/ConfirmModal'
import FormAlert from '@/components/FormAlert'
import FText from '@/components/FormFields/FText'
import FFile from '@/components/FormFields/FFile'
import FImagePreview from '@/components/FormFields/FImagePreview'
import FCheckbox from '@/components/FormFields/FCheckbox'
import FSearchSelect from '@/components/FormFields/FSearchSelect'
import EventFieldsSection from '@/components/Events/EventFieldsSection'
import EventFieldsNameSection from '@/components/Events/EventFieldsNameSection'
import DocModal from '@/components/Docs/DocModal'

const JUDGES_OPTIONS = [1, 2, 3].map(n => ({ id: n, name: String(n) }))

export default {
  name: 'EventForm',
  components: {
    ConfirmModal,
    FormAlert,
    FText,
    FFile,
    FImagePreview,
    FCheckbox,
    FSearchSelect,
    EventFieldsSection,
    EventFieldsNameSection,
    DocModal
  },
  props: {
    events: { type: Array, required: true },
    sharedForm: { type: Object, required: true },
    eventTypes: { type: Array, default: () => [] },
    loadingTypes: { type: Boolean, default: false },
    isCombined: { type: Boolean, default: false },
    brotherId: { type: Number, default: null },
    lockToggles: { type: Boolean, default: false },
    errors: { type: Object, default: () => ({}) },
    alert: { type: Object, default: () => ({ message: null, type: 'warning' }) },
    sponsorLogoUrl: { type: String, default: null },
    deletingLogo: { type: Boolean, default: false },
    availableJudges: { type: Array, default: () => [] },
    loadingJudges: { type: Boolean, default: false },
    judgeIds: { type: Array, default: () => [] }
  },
  data() {
    return {
      confirmPending: null,
      showGuide: false,
      checkboxKey: 0
    }
  },
  computed: {
    judgesOptions() {
      return JUDGES_OPTIONS
    },
    selectedType0Defaults() {
      const selected = this.eventTypes.find(t => t.id === this.events[0].event_type_id)
      return selected != null ? selected.defaults : {}
    },
    requiresHorse() {
      return this.selectedType0Defaults.requires_horse !== false
    },
    hasJudges() {
      return this.selectedType0Defaults.has_judges !== false
    },
    hasDeclaredPrize() {
      return this.selectedType0Defaults.has_declared_prize !== false
    },
    // Hidden is a fixed restriction: it can never be used in a combined event (backend
    // enforces this on both create and edit) — same rule as the no-horse-required types below,
    // just keyed by name instead of a capability flag since Hidden still requires a horse.
    isFirstEventHidden() {
      const type = this.eventTypes.find(t => t.id === this.events[0]?.event_type_id)
      return type?.name === 'Hidden'
    },
    firstEventCombinable() {
      return this.requiresHorse && !this.isFirstEventHidden
    },
    combinableEventTypes() {
      return this.eventTypes.filter(t => t.defaults.requires_horse !== false && t.name !== 'Hidden')
    }
  },
  methods: {
    onCombinedInput(val) {
      if (val) {
        this.confirmPending = 'combined'
      } else {
        this.$emit('toggle-combined', false)
      }
    },
    onConjunctionInput(val) {
      if (val) {
        this.confirmPending = 'conjunction'
      } else {
        this.$emit('update-shared', 'is_conjunction', false)
      }
    },
    onConfirm() {
      if (this.confirmPending === 'combined') {
        this.$emit('toggle-combined', true)
      } else if (this.confirmPending === 'conjunction') {
        this.$emit('update-shared', 'is_conjunction', true)
        this.$emit('update-shared', 'discount_price', null)
      }
      this.confirmPending = null
    },
    onCancelConfirm() {
      this.confirmPending = null
      this.checkboxKey += 1
    },
    sharedError(field) {
      return this.errors[field] ? this.errors[field][0] : null
    },
    judgeLabel(judge) {
      return `${judge.first_name} ${judge.last_name}`
    },
    onJudgesNumChange(val) {
      this.$emit('update-shared', 'judges_num', val != null ? val : 1)
    },
    onLogoChange(event) {
      const file = event.target.files[0]
      this.$emit('logo-change', file != null ? file : null)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Events/EventForm.vue